import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { map, mergeMap, share, tap } from 'rxjs/operators';
import { fiscalYearPeriod, gracePeriod } from 'src/properties.json';
import { UserProfileImageSizes } from '../../models/image-resize.enum';
import { ActivityParticipant } from '../../models/participant.model';
import { ActivityCauseModel } from '../models/activity-cause.model';
import { ActivityInvite } from '../models/activity-invite.model';
import { Activity, ActivityPost, ActivityPut } from '../models/activity-post.model';
import { ActivityStatus } from '../models/activity-status.enum';
import { ActivityBlueprint } from '../models/blueprint.model';
import { PaginationResponse } from '../models/pagination-response';
import { SearchUser } from '../models/search-user.model';
import { UserClaim } from '../models/user-claim.enum';

@Injectable()
export class CreateService {
    public isReady: ReplaySubject<boolean> = new ReplaySubject(0);
    public continueButtonDisabled = false;
    public bluePrintLoadedOnce = false;
    public serviceType: ReplaySubject<string> = new ReplaySubject(1);
    public currentActivity: ReplaySubject<any> = new ReplaySubject(1);
    public activityReady: ReplaySubject<any> = new ReplaySubject(1);
    public copyActivity: ReplaySubject<Activity> = new ReplaySubject();
    public currentBlueprint: ReplaySubject<ActivityBlueprint> = new ReplaySubject(
        1
    );
    public currentActivityImage: ReplaySubject<any> = new ReplaySubject(1);
    public currentStep: ReplaySubject<number> = new ReplaySubject(1);
    /**
     * This is the event emitter that should be used by each step in the wizard to perform async tasks
     * related to saving any work done on the current page
     */
    public continueClicked: EventEmitter<any> = new EventEmitter();
    /**
     * This is the event emitter that should be used once all async work is done and we are ready to
     * move on to the next page
     */
    public readyToContinue: EventEmitter<any> = new EventEmitter();
    public setContinueLabel: ReplaySubject<string> = new ReplaySubject(1);
    public backClicked: EventEmitter<any> = new EventEmitter();
    public currentParticipants: BehaviorSubject<any[]> = new BehaviorSubject([]);
    public currentMembers: BehaviorSubject<any[]> = new BehaviorSubject([]);
    public overrideStep: EventEmitter<number> = new EventEmitter();
    public continueText = '';
    public continueTextTranslation = '';
    public showSkipButtonForInvite: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public hideTopContinueButton = false;
    public hideBack = false;
    public saveActivity: EventEmitter<any> = new EventEmitter();
    private activityCauses: ActivityCauseModel[] = [];
    public crossButtonClickedOnShare: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public continueAfterCurrencyConversion = new BehaviorSubject(false);
    public currentYear;
    public currentDate;
    public currentFiscalYearStartDate;
    public currentFiscalYearEndDate;
    public gracePeriodStartDate;
    public gracePeriodEndDate;

    constructor(private http: HttpClient) {

        // this.currentDate = new Date(2020, 6, 15, 0, 0, 0, 0);
        this.currentDate = new Date();
        this.currentDate.setHours(0, 0, 0, 0);
        this.currentYear = this.currentDate.getFullYear();
        if (this.currentDate.getMonth() <= fiscalYearPeriod.endMonth - 1) {
            this.currentFiscalYearStartDate = new Date(this.currentYear - 1, fiscalYearPeriod.startMonth - 1, fiscalYearPeriod.startDate);
            this.currentFiscalYearEndDate = new Date(this.currentYear, fiscalYearPeriod.endMonth - 1, fiscalYearPeriod.endDate);
            this.gracePeriodStartDate = new Date(this.currentYear - 1, gracePeriod.startMonth - 1, gracePeriod.startDate);
            this.gracePeriodEndDate = new Date(this.currentYear - 1, gracePeriod.startMonth - 1, gracePeriod.endDate);
        } else {
            this.currentFiscalYearStartDate = new Date(this.currentYear, fiscalYearPeriod.startMonth - 1, fiscalYearPeriod.startDate);
            this.currentFiscalYearEndDate = new Date(this.currentYear + 1, fiscalYearPeriod.endMonth - 1, fiscalYearPeriod.endDate);
            this.gracePeriodStartDate = new Date(this.currentYear, gracePeriod.startMonth - 1, gracePeriod.startDate);
            this.gracePeriodEndDate = new Date(this.currentYear, gracePeriod.startMonth - 1, gracePeriod.endDate);
        }
    }

    /**
     * Returns a list of activity causes
     * @returns {Observable<ActivityCauseModel[]>}
     */
    public getActivityCauses(): Observable<ActivityCauseModel[]> {
        if (this.activityCauses.length) {
            return of(this.activityCauses);
        } else {
            return this.http.get('mobileapi://activities/activitycauses').pipe(
                tap((causes: ActivityCauseModel[]) => {
                    this.activityCauses = causes;
                })
            ) as Observable<ActivityCauseModel[]>;
        }
    }

    public getDistrictClubAndAssociatedClubs(companyId: number): Observable<any> {
        const params: any = {
            pageNumber: '1',
            pageSize: '1000',
            filter: `(CompanyId eq '${companyId}' or DistrictId eq ${companyId})`
        };

        return this.http.get(`mobileapi://companies/search`, { params });
    }

    public getClubById(clubId: number): Observable<any> {
        if (!clubId) {
            return of();
        }

        const filter = `(CompanyId eq '${clubId}')`;

        const params: any = {
            pageNumber: '1',
            pageSize: '1000',
            filter: filter
        };

        return this.http.get(`mobileapi://companies/search`, { params }).pipe(
            map((res: any) => {
                if (res.results.length === 0) {
                    return null;
                }

                const club = res.results[0].document;

                return club;
            })
        );
    }

    public getClubsById(clubIds: number[]): Observable<any[]> {
        if (!clubIds || clubIds.length === 0) {
            return of(null);
        }

        const filter = `(${clubIds
            .map(clubId => `CompanyId eq '${clubId}'`)
            .join(' or ')})`;

        const params: any = {
            pageNumber: '1',
            pageSize: '1000',
            filter: filter
        };

        return this.http.get(`mobileapi://companies/search`, { params }).pipe(
            map((res: any) => {
                let clubs = _(res.results)
                    .map(item => item.document)
                    .orderBy(['companyName', 'companyId'], ['asc', 'asc'])
                    .value();

                clubs = this.addCompanyIdToDuplicates(clubs);

                return clubs;
            })
        );
    }

    /**
     * Return a list of clubs by district id
     */
    public searchClubsByDistrict(districtId: number): Observable<any> {
        const params: any = {
            pageNumber: '1',
            pageSize: '1000',
            filter: `(DistrictId eq ${districtId} and (baseCompanyType eq 'Club' or baseCompanyType eq 'Leo'))`
        };

        return this.http.get(`mobileapi://companies/search`, { params }).pipe(
            map((res: any) => {
                let clubs = _(res.results)
                    .map(item => item.document)
                    .orderBy(['companyName', 'companyId'], ['asc', 'asc'])
                    .value();

                clubs = this.addCompanyIdToDuplicates(clubs);

                return clubs;
            })
        );
    }

    /**
     * Return a list of clubs by district id
     */
    public searchDistrictsByMultipleDistrictId(
        multipleDistrictId: number
    ): Observable<any> {
        const params: any = {
            pageNumber: '1',
            pageSize: '1000',
            filter: `(multipleDistrictId eq ${multipleDistrictId} and (baseCompanyType eq 'Club' or baseCompanyType eq 'Leo'))`
        };

        return this.getAllPages(params, []).pipe(
            map((res: any) => {
                let mapped = _(res)
                    .map(item => item.document)
                    .orderBy(['companyName', 'companyId'], ['asc', 'asc'])
                    .value();

                mapped = this.addCompanyIdToDuplicates(mapped);
                const districts = _(mapped)
                    .map(r => {
                        return { districtId: r.districtId, districtName: r.districtName };
                    })
                    .uniqBy('districtId')
                    .orderBy(['districtName', 'districtId'], ['asc', 'asc'])
                    .value();

                const groups = _.groupBy(mapped, r => r.districtId);

                const result = {
                    districts,
                    groups
                };

                return result;
            })
        );
    }

    public getAllPages(params: any, results: any[]): Observable<any[]> {
        return this.http.get(`mobileapi://companies/search`, { params }).pipe(
            mergeMap((res: any) => {
                const pageNum = parseInt(params.pageNumber, 10);
                const pageSize = parseInt(params.pageSize, 10);

                results = results.concat(res.results);

                if (res.count <= pageNum * pageSize) {
                    return of(results);
                }

                params.pageNumber = '' + (pageNum + 1);
                return this.getAllPages(params, results);
            })
        );
    }

    // Clubs must be sorted alphabetically by companyName or this algorithm will not work
    public addCompanyIdToDuplicates(clubs: any[]) {
        if (!clubs || clubs.length === 0) {
            return [];
        }

        let duplicatedName = '';

        for (let i = 0; i < clubs.length; i++) {
            const club = clubs[i];
            if (duplicatedName !== club.companyName) {
                duplicatedName = club.companyName;

                continue;
            }

            const prevClub = clubs[i - 1];
            if (prevClub.companyName === duplicatedName) {
                prevClub.companyName = `${prevClub.companyName} (${prevClub.companyId
                    })`;
            }

            club.companyName = `${club.companyName} (${club.companyId})`;
        }

        return clubs;
    }

    /**
     * Returns a list of ActivityBlueprints
     * @param {string} key
     * @returns {Observable<ActivityBlueprint[]>}
     */
    public getBlueprints(key: string): Observable<ActivityBlueprint[]> {
        const params: any = {
            activitycausetranslationkey: key
        };
        return this.http.get(`mobileapi://Blueprints`, { params }) as Observable<
            ActivityBlueprint[]
        >;
    }
    /**
     * Posts a new activity
     * @param {ActivityPost} activity
     * @returns {Observable<Activity>}
     */
    public createActivity(activity: ActivityPost): Observable<Activity> {
        return this.http.post(`mobileapi://Activities`, activity) as Observable<
            Activity
        >;
    }

    /**
     * Puts an activity
     * @param {number} activityId
     * @param {ActivityPut} activity
     * @returns {Observable<Activity>}
     */
    public updateActivity(
        activityId: number,
        activity: ActivityPut
    ): Observable<Activity> {
        return this.http.put(
            `mobileapi://activities/${activityId}`,
            activity
        ) as Observable<Activity>;
    }

    /**
     * Posts an activity participant
     * @param {number} activityId
     * @param {string} participantId
     * @returns {Observable<ActivityParticipant>}
     */
    public addActivityParticipant(
        activityId: number,
        participantId: string
    ): Observable<ActivityParticipant> {
        return this.http.post(
            `mobileapi://activities/${activityId}/participants`,
            `"${participantId}"`,
            {
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json'
                }
            }
        ) as Observable<ActivityParticipant>;
    }

    /**
     * Updates an activity attribute
     * @param {number} activityId
     * @param {number} attributeId
     * @param {number} attributeValue
     * @returns {Observable<any>}
     */
    public updateActivityAttribute(
        activityId: number,
        attributeId: number,
        attributeValue: number
    ): Observable<any> {
        const params = {
            attributeId,
            attributeValue
        };
        return this.http.post(
            `mobileapi://activities/${activityId}/attributes`,
            params
        );
    }

    public updateActivityAttributes(activityId: number, attributes: any[]): Observable<any> {
        return this.http.put(`mobileapi://activities/${activityId}/attributes`, attributes);
    }

    /**
     * Search for users
     * @param {string} searchText
     * @param {string} filter
     * @param {string} orderBy
     * @param {number} pageNumber
     * @param {number} pageSize
     * @returns {Observable<PaginationResponse<SearchUser>>}
     */
    public searchUsers(
        searchText?: string,
        filter?: string,
        pageNumber?: number,
        pageSize?: number
    ): Observable<PaginationResponse<SearchUser>> {
        const params: any = {};
        if (searchText) {
            params.searchText = searchText;
        }
        if (filter) {
            params.filter = filter;
        }
        if (pageNumber !== undefined) {
            params.pageNumber = pageNumber.toString();
        }
        if (pageSize !== undefined) {
            params.pageSize = pageSize.toString();
        }
        params.imageDimension = UserProfileImageSizes.image_50x50;
        return this.http.get('apirooturl://api/users/search', {
            params
        }) as Observable<PaginationResponse<SearchUser>>;
    }

    /**
     * Post to invite activity user
     * @param {number} activityId
     * @param {ActivityInvite} invitee
     * @returns {Observable<any>}
     */
    public activityInviteUser(
        activityId: number,
        invitee: ActivityInvite
    ): Observable<any> {
        return this.http.post(
            `mobileapi://activities/${activityId}/invitees`,
            invitee
        );
    }

    /**
     * Invites a group of members for an activity
     * @param {number} activityId
     * @param {ActivityInvite[]} inviteGroup
     * @returns {Observable<any>}
     */
    public activityInviteGroup(
        activityId: number,
        inviteGroup: ActivityInvite[]
    ): Observable<any> {
        return this.http.post(
            `mobileapi://activities/${activityId}/inviteegroup`,
            inviteGroup
        );
    }

    /**
     * Removes and invite group from an activity
     * @param {number} activityId
     * @param {ActivityInvite[]} inviteGroup
     * @returns {Observable<any>}
     */
    public activityRemoveInviteGroup(
        activityId: number,
        inviteeIds: Array<number>
    ): Observable<any> {
        return this.http.request(
            'delete',
            `mobileapi://activities/${activityId}/inviteegroup`,
            { body: inviteeIds }
        );
    }

    /**
     * Retrieves attribute keys for activity attributes
     * @returns {Observable<any>}
     */
    public getActivityAttributesKeys(): Observable<any> {
        return this.http.get('mobileapi://activityattributes?language=en');
    }

    /**
     * Activate an activity by id
     * @param {number} activityId
     * @returns {Observable<any>}
     */
    public activateActivity(activityId: number): Observable<any> {
        return this.http.put(`mobileapi://activities/${activityId}/activate`, {});
    }

    /**
     * Report an activity by id
     * @param {number} activityId
     * @returns {Observable<any>}
     */
    public reportActivity(activityId: number): Observable<any> {
        let timezoneOffset = Math.abs(new Date().getTimezoneOffset().valueOf()) * 60;
        return this.http.put(`mobileapi://activities/${activityId}/reported?timezoneOffsetInSeconds=${timezoneOffset}`, {});
    }

    /**
     * Uploads an activity image
     * @param {number} activityId
     * @param file
     * @returns {Observable<any>}
     */
    public uploadActivityImage(activityId: number, file: any): Observable<any> {
        return this.http.post(
            `mobileapi://activities/${activityId}/activityimage`,
            file
        );
    }

    public deleteActivityImage(activityId: number): Observable<any> {
        return this.http.delete(
            `mobileapi://activities/${activityId}/activityimage`
        );
    }

    public deleteImage(activityId: number, imageId: number): Observable<any> {
        return this.http.delete(
            `mobileapi://activities/${activityId}/images/${imageId}`
        );
    }

    public uploadActivityImages(activityId: number, file: any): Observable<any> {
        return this.http.post(
            `mobileapi://activities/${activityId}/images`,
            file
        );
    }
    public getActivityImages(activityId: number): Observable<any> {
        const imageObservable = this.http.get(
            `mobileapi://activities/${activityId}/images`
        );
        return imageObservable.pipe(share());
    }

    public changeActivityImage(activityId: number, photoUrlModel: any): Observable<any> {
        const changeImageObservable = this.http.post(
            `mobileapi://activities/${activityId}/activityimageurl`,
            photoUrlModel
        );
        return changeImageObservable.pipe(share());
    }
    public getCurrentFiscalYear(): any {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        let fiscalYearEnd;
        let fiscalYearStart;
        if (currentDate.getMonth() <= fiscalYearPeriod.endMonth - 1) {
            fiscalYearStart = new Date(currentYear - 1, fiscalYearPeriod.startMonth - 1, fiscalYearPeriod.startDate);
            fiscalYearEnd = new Date(currentYear, fiscalYearPeriod.endMonth - 1, fiscalYearPeriod.endDate);
        } else {
            fiscalYearStart = new Date(currentYear, fiscalYearPeriod.startMonth - 1, fiscalYearPeriod.startDate);
            fiscalYearEnd = new Date(currentYear + 1, fiscalYearPeriod.endMonth - 1, fiscalYearPeriod.endDate);
        }
        // const fiscalYearEnd = new Date(currentYear, 5, 30);
        // const fiscalYearStart = new Date(currentYear - 1, 6, 1);
        return { startDate: fiscalYearStart, endDate: fiscalYearEnd };
    }

    public isActivityEditable(activity: any, user: any) {
        if (activity.activityStatus === ActivityStatus.Active || activity.activityStatus === ActivityStatus.Draft) {
            return true;
        }
        const isUserOfficer = user.userProfile.isOfficer;
        const currentFiscalYear = this.getCurrentFiscalYear();
        const activityEndDate = moment(this.getActualDate(activity.endDate) || this.getActualDate(activity.startDate));
        // if (typeof endDate !== 'object') {
        //   endDate = new Date(endDate);
        // }
        let startFiscalYear = moment(this.getCurrentFiscalYear().startDate);
        const updateActivityClaims = this.getClaimValues(user.claims, [UserClaim.CanUpdateActivity]) || [];
        const companyId = parseInt(activity.activityCompanyId || activity.companyId || 0, 10);
        if (
            this.currentDate >=
            this.gracePeriodStartDate &&
            this.currentDate <=
            this.gracePeriodEndDate
        ) {
            let pastFiscalYearStartDate = moment(this.getPastFiscalYearStartDate())
            return (pastFiscalYearStartDate.isBefore(activityEndDate) && this.canUserReportActivityBasedOnClaims(activity, user)) ||
                (pastFiscalYearStartDate.isBefore(activityEndDate) && updateActivityClaims.includes(companyId));
        }


        return (startFiscalYear.isBefore(activityEndDate) && this.canUserReportActivityBasedOnClaims(activity, user)) ||
            (startFiscalYear.isBefore(activityEndDate) && updateActivityClaims.includes(companyId));
    }
    public canReportActivity(activity: any, user: any): boolean {
        const endDateString = this.getActualDate(activity.endDate) || this.getActualDate(activity.startDate);
        const endDate = moment(endDateString);
        const currentDate = moment(new Date());
        // can't report an activity that has not ended yet
        if (endDate.isAfter(currentDate)) {
            return false;
        }
        return this.canUserReportActivityBasedOnClaims(activity, user);
    }
    public canUserReportActivityBasedOnClaims(activity: any, user: any): boolean {
        let companyId = activity.activityCompanyId || activity.companyId;
        companyId = parseInt(companyId, 10);
        const districtId = parseInt(activity.activityDistrictId || activity.districtId || 0, 10);
        const multiDistrictId = parseInt(activity.activityMultiDistrictId || activity.multipleDistrictId || 0, 10);
        const companyClaimIds = this.getClaimValues(user.claims, [UserClaim.CanReportActivity]);
        const districtClaimIds =
            this.getClaimValues(user.claims, [UserClaim.CanReportDistrictActivity, UserClaim.CanReportClubActivitybyDistrict]);
        const multiDistrictClaimIds =
            this.getClaimValues(user.claims,
                [UserClaim.CanReportMDActivity,
                UserClaim.CanReportClubActivitybyMultiDistrict,
                UserClaim.CanReportDistrictActivityByMultiDistrict]);
        const exists =
            companyClaimIds.includes(companyId) ||
            districtClaimIds.includes(districtId) ||
            multiDistrictClaimIds.includes(multiDistrictId);
        return exists;
    }
    public getClaimValues(claims: any[], claimsToRetrieve: UserClaim[]): number[] {
        const claim = claims.filter(c => claimsToRetrieve.find(ctr => ctr === c.claimType));
        if (!claim || claim.length === 0) return [];
        const claimValues = _.flatMap(claim, c => {
            return c.claimValue.split(',');
        });
        const claimInt = claimValues.map(val => parseInt(val, 10));
        return claimInt;
    }
    getPastFiscalYearStartDate() {
        const currentMonth = this.currentDate.getMonth();
        let minDateYear;
        if (currentMonth <= fiscalYearPeriod.endMonth - 1) {
            minDateYear = this.currentYear - 2;
        } else {
            minDateYear = this.currentYear - 1;
        }
        const minDateMonth = fiscalYearPeriod.startMonth;
        const minDate = fiscalYearPeriod.startDate;
        return new Date(minDateYear, minDateMonth - 1, minDate, 0, 0, 0, 0);
    }

    getActualDate(date) {
        if (date && (typeof (date) != 'object')) {
            return date.replace('Z', '');
        }
    }

    checkPastActivity(ActivityDate) {
        const endDateString = this.getActualDate(ActivityDate);
        const endDate = moment(endDateString);
        const currentDate = moment(new Date());
        // can't report an activity that has not ended yet
        if (endDate.isAfter(currentDate)) {
            return false;
        }
        return true;

    }
}
